<template>
  <div class="brochure-viewer">
    <BrochureNavbar
      v-model:is-index-open="isIndexOpen"
      :title="title"
      :expire-after="brochureData?.brochure?.expireAfter"
      :active-from="brochureData?.brochure?.activeFrom"
      :total-pages="pagesData?.pages.length ?? 0"
      :shop-slug="brochureData?.brochure?.shop?.slug"
    />
    <BrochureIndex
      v-model:is-index-open="isIndexOpen"
      :pages="pagesData?.pages ?? []"
    />
    <BrochureDisplay
      v-if="brochureData?.brochure && !showLocationModal"
      :is-loading="$route.params.id !== brochureData?.brochure?.id"
      :pages="pagesData?.pages ?? []"
      :brochure-title="brochureData.brochure.name"
      :hotspot-mode="brochureData.brochure.hotspotMode"
    />
    <BrochureLocationModal v-if="showLocationModal" />
  </div>
</template>

<script setup lang="ts">
import { useBrochureQuery, usePagesQuery } from "~/graphql/generated";
import { event } from "vue-gtag";

onMounted(() => {
  event("brochure_open_web", { type: "default",  description: "A brochure has been opened." });
});

definePageMeta({
  layout: "brochure",
  key: (route) => (route.params.id as string) || "0", // So we don't re-render when the pagenumber changes
});

// Excuse the awfull routing here, but we need to keep the routes the same as the current site for APP-link purposes
false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    "nl-NL": "/[slug]-aanbiedingen/[id]/[[pagenumber]]",
    fr: "/[slug]-folder/[id]/[[pagenumber]]",
    nl: "/[slug]-folder/[id]/[[pagenumber]]",
  },
});

const route = useRoute();

const localeRoute = useLocaleRoute();
/** The canonical for a paginated brochure should always point to the 0 page */
const canonicalUrl = computed(() => {
  const path = localeRoute({
    name: "slug-offers-id-pagenumber",
    params: { slug: route.params.slug, id: route.params.id, pagenumber: 0 },
  })?.fullPath;
  return useRequestURL().host + path;
});
useHead({
  link: [
    {
      rel: "canonical",
      href: canonicalUrl.value,
    },
  ],
});
const isIndexOpen = ref(false);

// Get the brochure metadata
const { data: brochureData } = await useBrochureQuery({
  variables: {
    brochureInput: { id: route.params.id as string },
  },
});

const location = useLocationState();
const showLocationModal = computed(
  () => (brochureData.value?.brochure?.regional ?? false) && !location.value,
);

const pagesInput = computed(() => {
  return {
    pagesInput: {
      brochureId: route.params.id as string,
      ...(location.value && {
        point: {
          latitude: location.value.lat,
          longitude: location.value.long,
        },
      }),
    },
  };
});

// Get the brochure pages
const { data: pagesData } = await usePagesQuery({
  variables: pagesInput,
  pause: showLocationModal,
});

const { t, locale } = useI18n();

const title = computed(() =>
  brochureData.value?.brochure
    ? t("brochure.page-title", {
        name: brochureData.value.brochure.name,
        weekNumber: currentDateWeekNumber(),
      })
    : undefined,
);
const metaDescription = computed(() =>
  brochureData.value?.brochure
    ? t("brochure.page-description", {
        name: brochureData.value.brochure.name,
        activeFrom: dateToString(
          brochureData.value.brochure.activeFrom,
          locale.value,
        ),
        expireAfter: dateToString(
          brochureData.value.brochure.expireAfter,
          locale.value,
        ),
      })
    : "",
);

useSeoMeta({
  title: title.value,
  description: metaDescription,
  ogDescription: metaDescription,
});

// Set the data for the current active shop for the brochure viewer. We'll be using this to filter advertisements in an `Advertisement`.
const activeShopId = computed(() => {
  return brochureData.value?.brochure?.shop.id;
});
provide("brochureData", activeShopId);
</script>

<style lang="scss" scoped>
.brochure-viewer {
  height: 100%;
  width: 100%;
  display: grid;
  grid-template-rows: auto 1fr;
}
</style>
